import React, { memo, useEffect, useMemo, useCallback } from "react";
import "./LineageWrapperComponent.scss";
import configStructure from "./configStructure.js";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import AxiomLoader from "__AxiomComponents/AxiomUI/AxiomLoader/AxiomLoader.jsx";
import LineageComponent from "../LineageComponent/LineageComponent.js";
import PlatformJSONViewer from "../PlatformUI/PlatformJSONViewer";
import PlatformTableViewer from "../PlatformUI/PlatformTableViewer";
import AxiomModal from "__AxiomComponents/AxiomModal/AxiomModal";

function LineageWrapperComponent(props) {
  const [state, dispatch] = useAxiomState(
    {
      loading: true,
      pipelineData: [],
      viewData: [],
      mainKPIData: [],
      derivedKPIData: [],
      widgetData: [],
      dashboardData: [],
      selectedNode: null,
      showOnlySelected: props.showOnlySelected != null ? true : false,
      sortHighlighed: false,
      modalContent: null,
      defaultSelectedNode: null,
    },
    props.appName,
    props.appID,
    "LineageWrapperComponent",
    props.componentID
  );

  useEffect(() => {
    dispatch({ defaultSelectedNode: props.selectedNode });
    dispatch({}, "AxiomEvent", "mount");
  }, [dispatch, props.selectedNode]);

  const onIconClick = useCallback(
    (e, type, data) => {
      if (type === "pipeline") {
        data.config && dispatch({ modalContent: { type: "json", data: data.config } });
      } else if (type === "view") {
        data.config && dispatch({ modalContent: { type: "table", data: data.config } });
      } else if (type === "main-kpi") {
        data.config && dispatch({ modalContent: { type: "json", data: data.config } });
      } else if (type === "derived-kpi") {
        data.config && dispatch({ modalContent: { type: "json", data: data.config } });
      } else if (type === "widget") {
        data.config && dispatch({ modalContent: { type: "json", data: data.config } });
      } else {
        console.log("type:", type, "data: ", data);
      }
    },
    [dispatch]
  );

  const onElementClick = useCallback(
    (event, element) => {
      if (element.type === "customFlowNodeContainer") {
        return;
      }
      if (event.target.nodeName.toLowerCase() === "div") {
        console.log("click", element, event.target.nodeName, event.target);
        if (state.selectedNode == null || element.id !== state.selectedNode.id) {
          dispatch({ selectedNode: element, defaultSelectedNode: null });
        } else {
          dispatch({ selectedNode: null, defaultSelectedNode: null });
        }
      }
    },
    [state.selectedNode, dispatch]
  );

  const closeModel = useCallback(() => {
    dispatch({ modalContent: null });
  }, [dispatch]);

  const onFilterOptionToggle = useCallback(() => {
    props.showOnlySelected == null && dispatch({ showOnlySelected: !state.showOnlySelected });
  }, [dispatch, state.showOnlySelected, props.showOnlySelected]);

  const calculatedLineageData = useMemo(() => {
    // const lineageData = [];
    const hGap = 300;
    const vGap = 70;
    const hStart = 70;
    const vStart = 75;
    const datasourcePosition = hStart;
    const pipelinePosition = datasourcePosition + hGap;
    const viewPosition = pipelinePosition + hGap;
    const mainKPIPosition = viewPosition + hGap;
    const derivedKPIPosition = mainKPIPosition + hGap;
    const widgetPosition = derivedKPIPosition + hGap;
    const dashboardPosition = widgetPosition + hGap;

    let containerNodes = [];
    let lineageNodes = [];
    let lineageLinks = [];
    containerNodes.push({
      id: `datasource-node-container`,
      data: { label: "Data Sources" },
      position: { x: datasourcePosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper datasource-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    containerNodes.push({
      id: `pipeline-node-container`,
      data: { label: "Pipelines" },
      position: { x: pipelinePosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper pipeline-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    containerNodes.push({
      id: `view-node-container`,
      data: { label: "Data Tables" },
      position: { x: viewPosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper view-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    containerNodes.push({
      id: `mainKPI-node-container`,
      data: { label: "Main KPIs" },
      position: { x: mainKPIPosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper mainKPI-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    containerNodes.push({
      id: `derivedKPI-node-container`,
      data: { label: "Calculated KPIs" },
      position: { x: derivedKPIPosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper derivedKPI-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    containerNodes.push({
      id: `widget-node-container`,
      data: { label: "Widgets" },
      position: { x: widgetPosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper widget-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    containerNodes.push({
      id: `dashboard-node-container`,
      data: { label: "Dashboards" },
      position: { x: dashboardPosition - 25, y: vStart - 50 },
      type: "customFlowNodeContainer",
      className: "flow-node-container-wrapper dashboard-node-container",
      draggable: false,
      connectable: false,
      selectable: false,
    });

    let dataSources = [];
    state.pipelineData.forEach((pData, iKPI) => {
      dataSources = dataSources.concat(
        pData.nameOfDataSourcesUsed
          .split(",")
          .map((m) => m.trim())
          .filter((f) => f)
      );
    });

    dataSources = [...new Set(dataSources)];
    dataSources.forEach((ds, iKPI) => {
      // const yPos = vStart + vGap * iKPI;
      // if (maxHeight < yPos) maxHeight = yPos;
      lineageNodes.push({
        id: `datasource-${iKPI}`,
        data: { label: ds },
        position: { x: datasourcePosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });
    });

    state.pipelineData.forEach((pData, iKPI) => {
      // const yPos = vStart + vGap * iKPI;
      // if (maxHeight < yPos) maxHeight = yPos;
      lineageNodes.push({
        id: `pipeline-${pData.pk_transactionID}`,
        data: {
          label: pData.pipelineName,
          icon: pData.config && "ICON_OPENMODAL",
          onIconClick: (e) => onIconClick(e, "pipeline", pData),
        },
        position: { x: pipelinePosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });
    });

    state.viewData.forEach((vData, iKPI) => {
      // const yPos = vStart + vGap * iKPI;
      // if (maxHeight < yPos) maxHeight = yPos;
      lineageNodes.push({
        id: `view-${vData.viewId}`,
        data: {
          label: vData.name,
          icon: vData.config && "ICON_OPENMODAL",
          onIconClick: (e) => onIconClick(e, "view", vData),
        },
        position: { x: viewPosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });
    });

    state.mainKPIData.forEach((mKPI, iKPI) => {
      // const yPos = vStart + vGap * iKPI;
      // if (maxHeight < yPos) maxHeight = yPos;
      lineageNodes.push({
        id: `mkpi-${mKPI.kpiID}`,
        data: {
          label: mKPI.displayName,
          icon: mKPI.config && "ICON_OPENMODAL",
          onIconClick: (e) => onIconClick(e, "main-kpi", mKPI),
        },
        position: { x: mainKPIPosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });

      lineageLinks.push({
        id: `link_view-${mKPI.viewID}_mkpi-${mKPI.kpiID}`,
        source: `view-${mKPI.viewID}`,
        target: `mkpi-${mKPI.kpiID}`,
        animated: false,
      });
    });

    state.derivedKPIData.forEach((dKPI, iKPI) => {
      lineageNodes.push({
        id: `dkpi-${dKPI.kpiID}`,
        data: {
          label: dKPI.displayName,
          icon: dKPI.config && "ICON_OPENMODAL",
          onIconClick: (e) => onIconClick(e, "derived-kpi", dKPI),
        },
        position: { x: derivedKPIPosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });

      dKPI.mainKPI.forEach((mKPI) => {
        lineageLinks.push({
          id: `link_mkpi-${mKPI}_dkpi-${dKPI.kpiID}`,
          source: `mkpi-${mKPI}`,
          target: `dkpi-${dKPI.kpiID}`,
          animated: false,
        });
      });
    });

    state.pipelineData.forEach((pData, iKPI) => {
      lineageLinks.push({
        id: `link_pipeline-${pData.pk_transactionID}_view-${pData.fk_axiomTable_ViewID}`,
        source: `pipeline-${pData.pk_transactionID}`,
        target: `view-${pData.fk_axiomTable_ViewID}`,
        animated: false,
      });
      pData.nameOfDataSourcesUsed
        .split(",")
        .map((m) => m.trim())
        .forEach((ds) => {
          lineageLinks.push({
            id: `link_datasource-${dataSources.indexOf(ds)}_pipeline-${pData.pk_transactionID}`,
            source: `datasource-${dataSources.indexOf(ds)}`,
            target: `pipeline-${pData.pk_transactionID}`,
            animated: false,
          });
        });
    });

    state.widgetData.forEach((w, iKPI) => {
      lineageNodes.push({
        id: `widget-${w.widgetID}`,
        data: {
          label: w.displayName,
          icon: w.config && "ICON_OPENMODAL",
          onIconClick: (e) => onIconClick(e, "widget", w),
        },
        position: { x: widgetPosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });
      w.kpiIDs.forEach((kpiID) => {
        const splittedKPIID = kpiID.toString().toLowerCase().split("derived_");
        if (splittedKPIID.length > 1) {
          lineageLinks.push({
            id: `link_dkpi-${splittedKPIID[1]}_widget-${w.widgetID}`,
            source: `dkpi-${splittedKPIID[1]}`,
            target: `widget-${w.widgetID}`,
            animated: false,
          });
        } else {
          lineageLinks.push({
            id: `link_mkpi-${splittedKPIID[0]}_widget-${w.widgetID}`,
            source: `mkpi-${splittedKPIID[0]}`,
            target: `widget-${w.widgetID}`,
            animated: false,
          });
        }
      });
    });

    state.dashboardData.forEach((db, iKPI) => {
      lineageNodes.push({
        id: `dashboard-${iKPI}`,
        data: { label: db.displayName },
        position: { x: dashboardPosition },
        type: "customFlowNode",
        className: "general-node",
        draggable: false,
        connectable: false,
        selectable: false,
      });
      db.widgetIDs.forEach((widgetID) => {
        lineageLinks.push({
          id: `link_widget-${widgetID}_dashboard-${iKPI}`,
          source: `widget-${widgetID}`,
          target: `dashboard-${iKPI}`,
          animated: false,
        });
      });
    });

    const highlightedNodes = [];
    if (state.defaultSelectedNode) {
      highlightedNodes.push({
        id: `${state.defaultSelectedNode.type}-${state.defaultSelectedNode.id}`,
        direction: "source",
      });
      highlightedNodes.push({
        id: `${state.defaultSelectedNode.type}-${state.defaultSelectedNode.id}`,
        direction: "target",
      });
    } else if (state.selectedNode) {
      highlightedNodes.push({ id: state.selectedNode.id, direction: "source" });
      highlightedNodes.push({ id: state.selectedNode.id, direction: "target" });
    }
    let currentIndex = 0;
    while (currentIndex < highlightedNodes.length) {
      const currentSelectedNode = highlightedNodes[currentIndex];
      lineageNodes.forEach((ld) => {
        if (ld.id == currentSelectedNode.id) {
          if (currentIndex > 1) {
            ld.className = "selected-node";
          } else {
            ld.className = "main-selected-node";
          }
        }
      });
      lineageLinks.forEach((ld) => {
        if (ld.source === currentSelectedNode.id || ld.target === currentSelectedNode.id) {
          if (currentSelectedNode.direction == "source" && ld.target === currentSelectedNode.id) {
            ld.animated = true;
            highlightedNodes.push({ id: ld.source, direction: "source" });
          }
          if (currentSelectedNode.direction == "target" && ld.source === currentSelectedNode.id) {
            ld.animated = true;
            highlightedNodes.push({ id: ld.target, direction: "target" });
          }
        }
      });
      currentIndex++;
    }

    if (highlightedNodes.length > 0) {
      lineageNodes.forEach((ld) => {
        if (ld.className != "selected-node" && ld.className != "main-selected-node") {
          ld.className = "not-selected-node";
        }
      });
      if (state.showOnlySelected === true) {
        lineageNodes = lineageNodes.filter((ld) => {
          return ld.className == "selected-node" || ld.className == "main-selected-node";
        });
        lineageLinks = lineageLinks.filter((ld) => {
          return ld.animated == true;
        });
      } else {
        if (state.sortHighlighed === true) {
          lineageNodes = [
            ...lineageNodes.filter((ld) => {
              return ld.className == "main-selected-node";
            }),
            ...lineageNodes.filter((ld) => {
              return ld.className == "selected-node";
            }),
            ...lineageNodes.filter((ld) => {
              return ld.className == "not-selected-node";
            }),
          ];
        }
      }
    }

    let maxHeight = 0;
    let maxWidth = dashboardPosition + hGap;

    const yPosKey = {};
    lineageNodes.forEach((ld) => {
      const key = ld.id.split("-")[0];
      if (yPosKey[key] == null) {
        yPosKey[key] = vStart;
      }
      ld.position.y = yPosKey[key];
      yPosKey[key] += vGap;
      if (maxHeight < yPosKey[key]) {
        maxHeight = yPosKey[key];
      }
    });

    maxHeight += 100;
    return {
      lineageData: [...containerNodes, ...lineageNodes, ...lineageLinks],
      maxHeight,
      maxWidth,
    };
  }, [
    state.pipelineData,
    state.viewData,
    state.mainKPIData,
    state.derivedKPIData,
    state.widgetData,
    state.dashboardData,
    onIconClick,
    state.selectedNode,
    state.showOnlySelected,
    state.sortHighlighed,
    state.defaultSelectedNode,
  ]);

  return (
    <div className="LineageWrapperComponent">
      {state.loading && (
        <AxiomLoader background={"rgba(255, 255, 255, 0.4)"} block=".LineageWrapperComponent" />
      )}
      {calculatedLineageData && (
        <>
          <div className="lineage-header">
            <input
              type="checkbox"
              checked={state.showOnlySelected}
              onChange={onFilterOptionToggle}
            />
            Show data lineage for selected components
          </div>
          <LineageComponent
            appName={props.appName}
            appID={props.appID}
            componentID={0}
            lineageData={calculatedLineageData.lineageData}
            onElementClick={onElementClick}
            height={calculatedLineageData.maxHeight}
            width={calculatedLineageData.maxWidth}
          />
          {state.modalContent && (
            <AxiomModal isOpen={true} onClose={closeModel}>
              <div className="platform-list-modal-view">
                {state.modalContent.type === "json" ? (
                  <PlatformJSONViewer data={state.modalContent.data} />
                ) : (
                  <PlatformTableViewer data={state.modalContent.data} />
                )}
              </div>
            </AxiomModal>
          )}
        </>
      )}
    </div>
  );
}

export default memo(LineageWrapperComponent);
